import { useState } from "react";
import { FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "@/lib/api";
import { pick } from "@/components/Translate";
import { t } from "@/i18n";

// afterReply: the reply was just sent; ask whether to keep it as an OI (controlled via open/onOpenChange).
export function ReplyToSopDialog({ q, sops, api, reply, onDone, afterReply = false, open: ctlOpen, onOpenChange }) {
  const nav = useNavigate();
  const [ownOpen, setOwnOpen] = useState(false);
  const open = afterReply ? ctlOpen : ownOpen;
  const setOpen = afterReply ? onOpenChange : setOwnOpen;
  const [mode, setMode] = useState(afterReply ? "new" : "existing");
  const [sopId, setSopId] = useState("");
  const [busy, setBusy] = useState(false);
  const choices = sops.filter((s) => !["archived", "processing"].includes(s.status));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/questions/${q.id}/to-sop`, { mode, sop_id: mode === "existing" ? sopId : null, reply });
      toast.success(t(mode === "existing" ? "Added to the OI — redrafting for your review" : "New OI draft is being created"));
      setOpen(false);
      onDone();
      nav(`/sops/${data.sop_id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <>
      {!afterReply && (
        <button className="btn btn-ghost btn-sm" disabled={!reply.trim()} onClick={() => setOpen(true)} data-testid={`review-to-sop-button-${q.id}`}>
          <FilePlus2 size={14} /> {t("Add to OI base")}
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-surface)] text-[var(--text-primary)]" data-testid={afterReply ? "save-reply-dialog" : "to-sop-dialog"}>
          <DialogHeader><DialogTitle>{t(afterReply ? "Reply sent. Save this response as an OI?" : "Turn this reply into knowledge")}</DialogTitle></DialogHeader>
          <div className="stack-sm">
            <p className="text-sm muted">“{pick(q, "question", false)}” → <b className="text-[var(--text-primary)]">{reply}</b></p>
            <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === "new"} onChange={() => setMode("new")} data-testid="to-sop-mode-new" /> {t("Create a new OI draft")}</label>
            <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === "existing"} onChange={() => setMode("existing")} data-testid="to-sop-mode-existing" /> {t("Add to an existing OI (new draft version)")}</label>
            {mode === "existing" && (
              <select className="field" value={sopId} onChange={(e) => setSopId(e.target.value)} data-testid="to-sop-select">
                <option value="">{t("Choose a procedure…")}</option>
                {choices.map((s) => <option key={s.id} value={s.id}>{s.title} ({t(s.status)})</option>)}
              </select>
            )}
            <p className="text-xs faint">{t(afterReply ? "Saved answers come up next time anyone asks. It stays a draft until you publish it."
              : "The apprentice gets your reply now. The OI change stays a draft until you publish it — staff keep seeing the current published version.")}</p>
            <button className="btn btn-primary w-full" disabled={busy || (mode === "existing" && !sopId)} onClick={submit} data-testid="to-sop-submit-button">
              {t(busy ? "Saving…" : afterReply ? "Yes, save as OI" : "Save reply & update OI")}
            </button>
            {afterReply && <button className="btn btn-ghost w-full" disabled={busy} onClick={() => setOpen(false)} data-testid="to-sop-skip-button">{t("No, don't save")}</button>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
